/**
 * Orders Store - Zustand
 * 
 * Manages:
 * - Available orders list
 * - Active order state
 * - Order acceptance/decline
 * - Order status updates
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { logger } from '@/lib/logger';

export type OrderStatus = 
  | 'pending'
  | 'accepted'
  | 'rider_enroute'
  | 'picked_up'
  | 'out_for_delivery'
  | 'delivered'
  | 'cancelled'
  // Sprint 28: Issue states
  | 'issue_reported'
  | 'awaiting_resolution';

export interface OrderItem {
  id: string;
  name: string;
  quantity: number;
  price: number;
  vendorId: string;
  vendorName: string;
}

export interface Order {
  id: string;
  customer: {
    id: string;
    name: string;
    phone: string;
  };
  vendor: {
    id: string;
    name: string;
    address: string;
    location: {
      lat: number;
      lng: number;
    };
  };
  delivery: {
    address: string;
    location: {
      lat: number;
      lng: number;
    };
    instructions?: string;
  };
  items: OrderItem[];
  totalAmount: number;
  deliveryFee: number;
  status: OrderStatus;
  paymentMethod: 'cod' | 'gcash' | 'paymaya' | 'wallet';
  distance: number; // in km
  estimatedPickupTime?: string;
  estimatedDeliveryTime?: string;
  createdAt: string;
  acceptedAt?: string;
  pickedUpAt?: string;
  deliveredAt?: string;
  // Sprint 28: Assignment expiry
  assignmentExpiresAt?: string;
}

// Sprint 28: Incoming assignment (pending accept/reject)
export interface IncomingAssignment {
  order: Order;
  expiresAt: Date;
}

interface OrdersState {
  // Available orders (not yet accepted)
  availableOrders: Order[];
  
  // Active order (currently being delivered)
  activeOrder: Order | null;
  
  // Sprint 28: Incoming assignment (pending accept/reject)
  incomingAssignment: IncomingAssignment | null;
  
  // Order history (completed orders)
  orderHistory: Order[];
  
  // Loading states
  isLoadingAvailable: boolean;
  isAccepting: boolean;
  isUpdatingStatus: boolean;
  
  // Actions
  setAvailableOrders: (orders: Order[]) => void;
  addAvailableOrder: (order: Order) => void;
  removeAvailableOrder: (orderId: string) => void;
  
  acceptOrder: (orderId: string) => void;
  setActiveOrder: (order: Order | null) => void;
  updateOrderStatus: (orderId: string, status: OrderStatus) => void;
  
  completeActiveOrder: () => void;
  cancelOrder: (orderId: string) => void;
  
  setIsLoadingAvailable: (loading: boolean) => void;
  setIsAccepting: (accepting: boolean) => void;
  setIsUpdatingStatus: (updating: boolean) => void;
  
  // Sprint 28: Assignment handling
  setIncomingAssignment: (assignment: IncomingAssignment | null) => void;
  clearIncomingAssignment: () => void;
  
  clearOrders: () => void;
}

export const useOrdersStore = create<OrdersState>()(
  persist(
    (set, get) => ({
      availableOrders: [],
      activeOrder: null,
      incomingAssignment: null,
      orderHistory: [],
      isLoadingAvailable: false,
      isAccepting: false,
      isUpdatingStatus: false,

      setAvailableOrders: (orders) => {
        logger.info('Available orders updated', { count: orders.length });
        set({ availableOrders: orders });
      },

      addAvailableOrder: (order) => {
        const { availableOrders } = get();
        
        // Prevent duplicates
        if (availableOrders.some(o => o.id === order.id)) {
          logger.warn('Duplicate order ignored', { orderId: order.id });
          return;
        }

        logger.info('New order added', { orderId: order.id });
        set({ availableOrders: [order, ...availableOrders] });
      },

      removeAvailableOrder: (orderId) => {
        const { availableOrders } = get();
        set({ 
          availableOrders: availableOrders.filter(o => o.id !== orderId) 
        });
      },

      acceptOrder: (orderId) => {
        const { availableOrders, activeOrder } = get();
        
        // Prevent double-accept
        if (activeOrder) {
          logger.warn('Cannot accept order - already have active order', {
            newOrderId: orderId,
            activeOrderId: activeOrder.id,
          });
          return;
        }

        const order = availableOrders.find(o => o.id === orderId);
        if (!order) {
          logger.error('Order not found for acceptance', new Error(`Order ${orderId} not found`));
          return;
        }

        // Update order status
        const acceptedOrder: Order = {
          ...order,
          status: 'accepted',
          acceptedAt: new Date().toISOString(),
        };

        logger.info('Order accepted', { orderId });
        
        set({
          activeOrder: acceptedOrder,
          availableOrders: availableOrders.filter(o => o.id !== orderId),
        });
      },

      setActiveOrder: (order) => {
        set({ activeOrder: order });
      },

      updateOrderStatus: (orderId, status) => {
        const { activeOrder, orderHistory } = get();
        
        if (activeOrder?.id === orderId) {
          const updatedOrder: Order = {
            ...activeOrder,
            status,
            ...(status === 'picked_up' && { pickedUpAt: new Date().toISOString() }),
            ...(status === 'delivered' && { deliveredAt: new Date().toISOString() }),
          };

          logger.info('Order status updated', { orderId, status });

          // If delivered, move to history
          if (status === 'delivered') {
            set({
              activeOrder: null,
              orderHistory: [updatedOrder, ...orderHistory],
            });
          } else {
            set({ activeOrder: updatedOrder });
          }
        }
      },

      completeActiveOrder: () => {
        const { activeOrder, orderHistory } = get();
        
        if (activeOrder) {
          logger.info('Order completed', { orderId: activeOrder.id });
          
          set({
            activeOrder: null,
            orderHistory: [
              { ...activeOrder, status: 'delivered', deliveredAt: new Date().toISOString() },
              ...orderHistory,
            ],
          });
        }
      },

      cancelOrder: (orderId) => {
        const { activeOrder, availableOrders } = get();
        
        if (activeOrder?.id === orderId) {
          logger.info('Active order cancelled', { orderId });
          set({ activeOrder: null });
        } else {
          logger.info('Available order removed', { orderId });
          set({ 
            availableOrders: availableOrders.filter(o => o.id !== orderId) 
          });
        }
      },

      setIsLoadingAvailable: (loading) => set({ isLoadingAvailable: loading }),
      setIsAccepting: (accepting) => set({ isAccepting: accepting }),
      setIsUpdatingStatus: (updating) => set({ isUpdatingStatus: updating }),

      // Sprint 28: Incoming assignment handling
      setIncomingAssignment: (assignment) => {
        if (assignment) {
          logger.info('Incoming assignment set', { 
            orderId: assignment.order.id,
            expiresAt: assignment.expiresAt.toISOString(),
          });
        }
        set({ incomingAssignment: assignment });
      },

      clearIncomingAssignment: () => {
        logger.info('Incoming assignment cleared');
        set({ incomingAssignment: null });
      },

      clearOrders: () => {
        logger.info('Orders cleared');
        set({
          availableOrders: [],
          activeOrder: null,
          incomingAssignment: null,
          orderHistory: [],
        });
      },
    }),
    {
      name: 'rider-orders-storage',
      partialize: (state) => ({
        activeOrder: state.activeOrder,
        orderHistory: state.orderHistory,
      }),
    }
  )
);

